import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Search.css';
import logo from "../../asset/EssilorLuxottica.svg";
import CofProjectDati, { Project } from '../TableAPI/CofProjectDati';
import { BASE_API } from '../TableAPI/Constant';
import { Box, Flex, Image, Button, Input, Text, Stack } from "@chakra-ui/react";
import ClockBar from './ClockBar';

const Search = () => {
  // State per gestire lo stato della tabella
  const [showTable, setShowTable] = useState(false);
  // State per memorizzare i dati recuperati dall'API
  const [dataCof, setDataCof] = useState<Project[]>([]);
  // State per memorizzare i dati selezionati dall'utente
  const [editableData, setEditableData] = useState<Project[]>([]);
  // State per memorizzare l'ID del progetto selezionato
  const [projectId, setProjectId] = useState<string>('');
  // State per memorizzare il valore dell'input di ricerca
  const [inputValue, setInputValue] = useState<string>('');
  // State per memorizzare le suggerimenti di ricerca
  const [suggestions, setSuggestions] = useState<Project[]>([]);
  // State per memorizzare eventuali messaggi di errore
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Effetto per recuperare i dati iniziali dall'API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const mainProjectId = sessionStorage.getItem('mainProjectId') || '';
        const response = await axios.get(`${BASE_API}/cofProjects/current/${mainProjectId}/`);
        const responseData = response.data;
        if (responseData && responseData.data) {
          setDataCof(responseData.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Funzione per gestire il cambiamento dell'input di ricerca
  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (value.trim() === '') {
      setSuggestions([]);
      setErrorMessage('');
      setShowTable(false); // Nascondi la tabella quando la barra di ricerca è vuota
      return;
    }
    const filteredSuggestions = dataCof.filter(project =>
      (project.form_details.NAME && project.form_details.NAME.toLowerCase().includes(value.toLowerCase())) ||
      project.project_id.toString().includes(value.toLowerCase())
    );
    setSuggestions(filteredSuggestions);
    if (filteredSuggestions.length === 0) {
      setErrorMessage('No suggestions found');
    } else {
      setErrorMessage('');
    }
  }

  // Funzione per gestire la selezione di un suggerimento di ricerca
  const handleSelectSuggestion = (selectedProject: Project) => {
    setInputValue(`${selectedProject.form_details.NAME} (${selectedProject.project_id})`);
    setEditableData([selectedProject]);
    setShowTable(false); // Nascondi temporaneamente la tabella per forzare un re-render
    setProjectId(selectedProject.project_id.toString());
    setSuggestions([]);
    // Ritarda l'aggiornamento dello stato per assicurarsi che la tabella venga re-renderizzata
    setTimeout(() => setShowTable(true), 0);
  }

  // Funzione per gestire il reindirizzamento alla pagina di ricerca quando si fa clic sul logo
  const handleLogoClick = () => {
    // Redirect alla pagina di ricerca
    window.location.href = '/search';
  };

  return (
    <Box>
      {/* Header con il logo e il link per l'upload */}
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mb={7}
        p={4}
        bg="gray.100"
        borderRadius="md"
        boxShadow="md"
      >
        <Image
          src={logo}
          alt=""
          cursor="pointer"
          onClick={handleLogoClick}
        />

        <Box justifyContent="center" ml={-60}>
          <ClockBar />
        </Box>

        <Button
          as="a"
          href='/upload'
          role="button"
          bg="#414042"
          color="white"
          p={3}
          borderRadius="md"
          boxShadow="md"
          _hover={{ bg: "#E6E7E8", color: "black", boxShadow: "lg", transform: "scale(1.05)" }}
          _focus={{ boxShadow: "outline" }}
        >
          UPLOAD
        </Button>
      </Flex>

      {/* Barra di ricerca */}
      <Box mb={4}>
        <Input
          placeholder="Type Project Name or Project Code"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          bg="white"
          color="black"
          border="2px"
          borderColor="gray.300"
          _placeholder={{ color: '#BCBEC0' }}
          p={3}
          width={{ base: "100%", md: "600px", lg: "800px" }}
        />

        {/* Mostra eventuali messaggi di errore */}
        {errorMessage && <Text color="#EE312F" mt={3}>{errorMessage}</Text>}

        {/* Mostra i suggerimenti di ricerca */}
        {suggestions.length > 0 && (
          <Stack spacing={1} mt={2} borderWidth={1} borderRadius="md" borderColor="gray.200" bg="white">
            {suggestions.map(project => (
              <Box
                key={project._id}
                onClick={() => handleSelectSuggestion(project)}
                cursor='pointer'
                textDecoration='underline'
                _hover={{ backgroundColor: '#f0f0f0' }}
                p={3}
              >
                {project.form_details.NAME} ({project.project_id})
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Mostra la tabella dei progetti */}
      {showTable && <CofProjectDati projectId={projectId} />}
    </Box>
  );
};

export default Search;
